import { useState, useEffect } from "react";
import { BeatCard } from "@/components/BeatCard";
import { BeatFilters } from "@/components/BeatFilters";
import { BeatDetailsModal } from "@/components/BeatDetailsModal";
import { UserMenu } from "@/components/UserMenu";
import { AuthModal } from "@/components/AuthModal";
import { PurchaseHistoryModal } from "@/components/PurchaseHistoryModal";
import { AddBeatModal } from "@/components/AddBeatModal";
import { AdminFeaturedBeatsModal } from "@/components/AdminFeaturedBeatsModal";
import { useAudioPlayer } from "@/components/AudioPlayerProvider";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface Beat {
  id: string;
  title: string;
  producer: string;
  genre: string;
  bpm: number;
  image_url: string;
  mp3_url: string;
  wav_url: string;
  flp_url: string;
  mp3_lease_price: number;
  wav_lease_price: number;
  flp_price: number;
  semi_exclusive_price: number;
  full_exclusive_price: number;
  is_featured: boolean;
  is_sold_exclusive?: boolean;
}

const AllBeats = () => {
  const navigate = useNavigate();
  const audioPlayer = useAudioPlayer();
  const { playBeat, setBeats: setAudioPlayerBeats, currentBeat, isPlaying } = audioPlayer || {};
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [bpmRange, setBpmRange] = useState<[number, number]>([60, 200]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBeat, setSelectedBeat] = useState<Beat | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allBeats, setAllBeats] = useState<Beat[]>([]);
  const [filteredBeats, setFilteredBeats] = useState<Beat[]>([]);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isPurchaseHistoryOpen, setIsPurchaseHistoryOpen] = useState(false);
  const [isAddBeatModalOpen, setIsAddBeatModalOpen] = useState(false);
  const [isFeaturedBeatsModalOpen, setIsFeaturedBeatsModalOpen] = useState(false);

  useEffect(() => {
    fetchBeats();
  }, []);

  const fetchBeats = async () => {
    const { data } = await supabase
      .from("beats")
      .select("*")
      .eq("is_deleted", false)
      .eq("is_hidden", false)
      .or(`release_date.is.null,release_date.lte.${new Date().toISOString()}`)
      .order("created_at", { ascending: false });
    
    if (data) {
      // Check for sold exclusives and mark them
      try {
        const { data: soldData } = await supabase.functions.invoke('get-sold-exclusives');
        const soldExclusiveBeatIds = new Set(soldData?.soldExclusiveBeatIds || []);
        
        const beatsWithSoldStatus = data.map(beat => ({
          ...beat,
          is_sold_exclusive: soldExclusiveBeatIds.has(beat.id)
        }));
        
        setAllBeats(beatsWithSoldStatus);
        setFilteredBeats(beatsWithSoldStatus);
        if (setAudioPlayerBeats) {
          setAudioPlayerBeats(beatsWithSoldStatus);
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error('Error checking sold exclusives:', error);
        setAllBeats(data);
        setFilteredBeats(data);
        if (setAudioPlayerBeats) {
          setAudioPlayerBeats(data);
        }
      }
    }
  };

  // Get unique genres from beats
  const availableGenres = [...new Set(allBeats.map(beat => beat.genre))];

  // Filter beats based on selected filters
  useEffect(() => {
    const filtered = allBeats.filter(beat => {
      const genreMatch = selectedGenres.length === 0 || selectedGenres.includes(beat.genre);
      const bpmMatch = beat.bpm >= bpmRange[0] && beat.bpm <= bpmRange[1];
      const searchMatch = searchQuery === "" || 
        beat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        beat.producer.toLowerCase().includes(searchQuery.toLowerCase());
      return genreMatch && bpmMatch && searchMatch;
    });
    setFilteredBeats(filtered);
  }, [allBeats, selectedGenres, bpmRange, searchQuery]);

  const handlePlayBeat = (beat: Beat) => {
    // Don't allow playing sold exclusive beats
    if (beat.is_sold_exclusive) {
      return;
    }
    if (playBeat) {
      playBeat(beat);
    }
  };

  const handleBeatClick = (beat: Beat) => {
    setSelectedBeat(beat);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-dark pb-24">
      {/* Header */}
      <header className="border-b border-border/20 bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                size="sm"
                className="shrink-0"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <h1 className="text-3xl font-bold gradient-text drop-shadow-sm">
                All Beats ({filteredBeats.length})
              </h1>
            </div>
            <UserMenu 
              onAuthClick={() => setIsAuthModalOpen(true)}
              onPurchasesClick={() => setIsPurchaseHistoryOpen(true)}
              onAddBeatClick={() => setIsAddBeatModalOpen(true)}
              onFeaturedBeatsClick={() => setIsFeaturedBeatsModalOpen(true)}
            />
          </div>
        </div>
      </header>

      {/* Filters and Beats Grid */}
      <main className="container mx-auto px-4 py-8">
        {/* Filters */}
        <BeatFilters
          genres={availableGenres}
          selectedGenres={selectedGenres}
          bpmRange={bpmRange}
          searchQuery={searchQuery}
          onGenresChange={setSelectedGenres}
          onBpmChange={setBpmRange}
          onSearchChange={setSearchQuery}
        />

        {/* Beat Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredBeats.filter(beat => !beat.is_sold_exclusive).map((beat, index) => (
            <div
              key={beat.id}
              className="animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <BeatCard 
                beat={beat}
                onPlay={() => handlePlayBeat(beat)}
                isPlaying={currentBeat?.id === beat.id && Boolean(isPlaying)}
                onClick={() => handleBeatClick(beat)}
              />
            </div>
          ))}
        </div>

        {/* Sold Exclusives Section */}
        {filteredBeats.filter(beat => beat.is_sold_exclusive).length > 0 && (
          <section className="mt-16">
            <h2 className="text-3xl font-bold gradient-text mb-8 text-center">
              Sold Exclusives
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBeats.filter(beat => beat.is_sold_exclusive).map((beat, index) => (
                <div
                  key={beat.id}
                  className="animate-fade-in opacity-60"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <BeatCard 
                    beat={beat}
                    onPlay={() => {}} // No play for sold exclusives
                    isPlaying={false}
                    onClick={() => handleBeatClick(beat)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {filteredBeats.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              No beats found matching your filters. Try adjusting your search criteria.
            </p>
          </div>
        )}

        {/* Beat Details Modal */}
        <BeatDetailsModal 
          beat={selectedBeat}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onBeatDeleted={fetchBeats}
        />

        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
        />

        <PurchaseHistoryModal
          isOpen={isPurchaseHistoryOpen}
          onClose={() => setIsPurchaseHistoryOpen(false)}
        />

        <AddBeatModal
          isOpen={isAddBeatModalOpen}
          onClose={() => setIsAddBeatModalOpen(false)}
          onBeatAdded={fetchBeats}
        />

        <AdminFeaturedBeatsModal
          isOpen={isFeaturedBeatsModalOpen}
          onClose={() => setIsFeaturedBeatsModalOpen(false)}
          onBeatsUpdated={fetchBeats}
        />
      </main>
    </div>
  );
};

export default AllBeats;